import { useState } from 'react';
import * as z from 'zod';
import ms from 'ms';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { updateOrganization } from '@/api/organization.api.private';
import { useOrganizationContext } from '../organization.context';
import DurationField, { DAY_MS, MINUTE_MS, durationMs } from './duration-field';

const DEFAULTS = {
  expiryAccessToken: 1,
  expiryRefreshToken: 90,
  refreshTokenRegenerateThreshold: 0,
};

const SCHEMA = z.object({
  expiryAccessToken: durationMs({ min: 1 * MINUTE_MS, max: 1 * DAY_MS }),
  expiryRefreshToken: durationMs({ min: 1 * DAY_MS, max: 120 * DAY_MS }),
  refreshTokenRegenerateThreshold: durationMs({ min: 0, max: 45 * DAY_MS }),
});

const buildDefaultValues = organization => {
  const conn = organization.internalConfigurations?.connection ?? {};
  return {
    expiryAccessToken: ms((conn.expiryAccessToken ?? DEFAULTS.expiryAccessToken) * DAY_MS),
    expiryRefreshToken: ms((conn.expiryRefreshToken ?? DEFAULTS.expiryRefreshToken) * DAY_MS),
    refreshTokenRegenerateThreshold: ms(
      (conn.refreshTokenRegenerateThreshold ?? DEFAULTS.refreshTokenRegenerateThreshold) * DAY_MS,
    ),
  };
};

export default function ConnectionSettings() {
  const { organization, setOrganization } = useOrganizationContext();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(SCHEMA),
    defaultValues: buildDefaultValues(organization),
  });

  const onSubmit = async values => {
    setLoading(true);
    const payload = {
      internalConfigurations: {
        connection: {
          expiryAccessToken: ms(values.expiryAccessToken) / DAY_MS,
          expiryRefreshToken: ms(values.expiryRefreshToken) / DAY_MS,
          refreshTokenRegenerateThreshold: ms(values.refreshTokenRegenerateThreshold) / DAY_MS,
        },
      },
    };

    const { data, error } = await updateOrganization({ id: organization.id, data: payload });
    setLoading(false);

    if (error) {
      toast({
        status: 'error',
        title: 'Failed to update connection settings',
        description: data?.message ?? 'An error occurred while saving the settings',
        variant: 'destructive',
      });
      return;
    }

    setOrganization(prev => ({ ...prev, ...data }));
    form.reset(buildDefaultValues(data));
    toast({
      status: 'success',
      title: 'Connection settings updated',
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign-in & sessions</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DurationField
              name="expiryAccessToken"
              label="Background session refresh"
              description="How often the app silently refreshes a signed-in user's session in the background. Users never see this — it's a technical setting that rarely needs changing. (range: 1 minute – 1 day)"
              note="Should only be changed by the dev team."
              placeholder="12h"
            />
            <DurationField
              name="expiryRefreshToken"
              label="Session length"
              description="How long a signed-in session lasts before the user must do a full OTP login again. (range: 1 – 120 days)"
              placeholder="90d"
            />
            <DurationField
              name="refreshTokenRegenerateThreshold"
              label="Keep active users signed in"
              description="Automatically extends the session of users who keep using the app, so they aren't logged out mid-use. When less than this much time is left, the session renews. Set to 0 to turn off. (range: 0 – 45 days)"
              note="Should only be changed by the dev team."
              placeholder="0"
            />
            <Button type="submit" loading={loading} disabled={loading}>
              Save
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
